import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit2, GitBranch, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { branchesApi } from '@/api/branches';
import { Badge, Button, EmptyState, Input, Loading, Modal, Select } from '@/components/ui';
import type { OrgBranch, Organization } from '@/types';

interface Props {
  org: Organization;
}

type BranchFormState = {
  name: string;
  code: string;
  country: string;
  customCountry: string;
  city: string;
  address: string;
  phone: string;
  email: string;
  timezone: string;
  currency: string;
  status: string;
};

const CURRENCY_OPTIONS = [
  { value: 'ILS', label: 'ILS' },
  { value: 'EUR', label: 'EUR' },
  { value: 'USD', label: 'USD' },
];

const COUNTRY_OPTIONS = [
  { value: 'IL', label: 'IL' },
  { value: 'NL', label: 'NL' },
  { value: 'other', label: 'אחר (טקסט חופשי)' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'פעיל' },
  { value: 'inactive', label: 'לא פעיל' },
];

function slugifyBranchCode(input: string) {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

function toNullable(value: string) {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function statusVariant(status: string): 'success' | 'neutral' {
  return status === 'active' ? 'success' : 'neutral';
}

function toFormState(branch?: OrgBranch): BranchFormState {
  const country = branch?.country ?? '';
  const isPresetCountry = country === 'IL' || country === 'NL' || !country;
  return {
    name: branch?.name ?? '',
    code: branch?.code ?? '',
    country: isPresetCountry ? country : 'other',
    customCountry: isPresetCountry ? '' : country,
    city: branch?.city ?? '',
    address: branch?.address ?? '',
    phone: branch?.phone ?? '',
    email: branch?.email ?? '',
    timezone: branch?.timezone ?? '',
    currency: branch?.currency ?? 'ILS',
    status: branch?.status ?? 'active',
  };
}

function BranchModal({
  orgId,
  branch,
  isOpen,
  onClose,
  onSaved,
}: {
  orgId: string;
  branch: OrgBranch | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<BranchFormState>(toFormState());
  const [codeTouched, setCodeTouched] = useState(false);
  const isEdit = !!branch;

  useEffect(() => {
    const next = toFormState(branch ?? undefined);
    setForm(next);
    setCodeTouched(!!branch);
  }, [branch, isOpen]);

  const createMutation = useMutation({
    mutationFn: () =>
      branchesApi.create({
        organization_id: orgId,
        name: form.name.trim(),
        code: form.code.trim(),
        country: form.country === 'other' ? toNullable(form.customCountry) : toNullable(form.country),
        city: toNullable(form.city),
        address: toNullable(form.address),
        phone: toNullable(form.phone),
        email: toNullable(form.email),
        timezone: toNullable(form.timezone),
        currency: form.currency,
        status: form.status,
      }),
    onSuccess: () => {
      toast.success('הסניף נוצר בהצלחה');
      onSaved();
      onClose();
    },
    onError: (e: Error) => toast.error(`שגיאה ביצירת סניף: ${e.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      branchesApi.update(branch!.id, {
        name: form.name.trim(),
        code: form.code.trim(),
        country: form.country === 'other' ? toNullable(form.customCountry) : toNullable(form.country),
        city: toNullable(form.city),
        address: toNullable(form.address),
        phone: toNullable(form.phone),
        email: toNullable(form.email),
        timezone: toNullable(form.timezone),
        currency: form.currency,
        status: form.status,
      }),
    onSuccess: () => {
      toast.success('הסניף עודכן בהצלחה');
      onSaved();
      onClose();
    },
    onError: (e: Error) => toast.error(`שגיאה בעדכון סניף: ${e.message}`),
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const canSave = useMemo(() => {
    const hasName = form.name.trim().length > 0;
    const hasCode = form.code.trim().length > 0;
    const hasCountry = form.country === 'other' ? form.customCountry.trim().length > 0 : true;
    return hasName && hasCode && hasCountry;
  }, [form]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'עריכת סניף' : 'הוספת סניף'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            ביטול
          </Button>
          <Button
            onClick={() => (isEdit ? updateMutation.mutate() : createMutation.mutate())}
            loading={isSaving}
            disabled={!canSave}
          >
            {isEdit ? 'שמירת שינויים' : 'יצירת סניף'}
          </Button>
        </>
      }
    >
      <div dir="rtl" className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="שם סניף"
          value={form.name}
          onChange={(e) => {
            const name = e.target.value;
            setForm((prev) => ({
              ...prev,
              name,
              code: codeTouched ? prev.code : slugifyBranchCode(name),
            }));
          }}
          placeholder="למשל: תל אביב"
          required
        />
        <Input
          label="קוד סניף"
          value={form.code}
          onChange={(e) => {
            setCodeTouched(true);
            setForm((prev) => ({ ...prev, code: slugifyBranchCode(e.target.value) }));
          }}
          placeholder="tel-aviv"
          required
          dir="ltr"
        />
        <Select
          label="מדינה"
          value={form.country}
          onChange={(e) => setForm((prev) => ({ ...prev, country: e.target.value }))}
          options={COUNTRY_OPTIONS}
          placeholder="בחר מדינה..."
        />
        {form.country === 'other' && (
          <Input
            label="מדינה (טקסט חופשי)"
            value={form.customCountry}
            onChange={(e) => setForm((prev) => ({ ...prev, customCountry: e.target.value }))}
            placeholder="לדוגמה: DE"
            required
          />
        )}
        <Input
          label="עיר"
          value={form.city}
          onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
          placeholder="עיר"
        />
        <Input
          label="טלפון"
          value={form.phone}
          onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
          placeholder="0500000000"
          dir="ltr"
        />
        <Input
          label="אימייל"
          type="email"
          value={form.email}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
          placeholder="branch@org.com"
          dir="ltr"
        />
        <Input
          label="אזור זמן"
          value={form.timezone}
          onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
          placeholder="Asia/Jerusalem"
          dir="ltr"
        />
        <Input
          label="כתובת"
          value={form.address}
          onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
          placeholder="רחוב ומספר"
          className="md:col-span-2"
        />
        <Select
          label="מטבע"
          value={form.currency}
          onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value }))}
          options={CURRENCY_OPTIONS}
        />
        <Select
          label="סטטוס"
          value={form.status}
          onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
          options={STATUS_OPTIONS}
        />
      </div>
    </Modal>
  );
}

export default function OrgBranchesTab({ org }: Props) {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<OrgBranch | null>(null);

  const { data: branches, isLoading } = useQuery({
    queryKey: ['org-branches', org.id],
    queryFn: () => branchesApi.list(org.id),
  });

  const deactivateMutation = useMutation({
    mutationFn: (branchId: string) => branchesApi.update(branchId, { status: 'inactive' }),
    onSuccess: () => {
      toast.success('הסניף הועבר ללא פעיל');
      queryClient.invalidateQueries({ queryKey: ['org-branches', org.id] });
    },
    onError: (e: Error) => toast.error(`שגיאה בעדכון סניף: ${e.message}`),
  });

  const openCreate = () => {
    setEditingBranch(null);
    setIsModalOpen(true);
  };

  const openEdit = (branch: OrgBranch) => {
    setEditingBranch(branch);
    setIsModalOpen(true);
  };

  const onSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['org-branches', org.id] });
  };

  return (
    <div dir="rtl" className="space-y-4">
      <div className="card p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-ink-primary">סניפים</h3>
            <p className="text-xs text-ink-muted mt-1">ניהול סניפי הארגון</p>
          </div>
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-3.5 w-3.5" />
            הוסף סניף
          </Button>
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-6">
            <Loading text="טוען סניפים..." />
          </div>
        ) : !branches?.length ? (
          <div className="p-8">
            <EmptyState
              icon={<GitBranch className="h-10 w-10 text-slate-600" />}
              title="אין סניפים לארגון"
              description="ניתן להוסיף סניף חדש כדי להתחיל"
            />
          </div>
        ) : (
          <>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-navy-border text-ink-muted">
                    <th className="text-right p-3 font-medium">שם</th>
                    <th className="text-right p-3 font-medium">קוד</th>
                    <th className="text-right p-3 font-medium">מדינה</th>
                    <th className="text-right p-3 font-medium">עיר</th>
                    <th className="text-right p-3 font-medium">מטבע</th>
                    <th className="text-right p-3 font-medium">סטטוס</th>
                    <th className="text-right p-3 font-medium">פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {branches.map((branch) => (
                    <tr key={branch.id} className="border-b border-navy-border/70 last:border-b-0">
                      <td className="p-3 text-ink-primary">
                        <div className="flex items-center gap-2">
                          <span>{branch.name}</span>
                          {branch.is_main && <Badge variant="warning">ראשי</Badge>}
                        </div>
                      </td>
                      <td className="p-3 text-ink-secondary num">{branch.code}</td>
                      <td className="p-3 text-ink-secondary">{branch.country ?? '—'}</td>
                      <td className="p-3 text-ink-secondary">{branch.city ?? '—'}</td>
                      <td className="p-3 text-ink-secondary">{branch.currency}</td>
                      <td className="p-3">
                        <Badge variant={statusVariant(branch.status)}>
                          {branch.status === 'active' ? 'פעיל' : branch.status}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(branch)}>
                            <Edit2 className="h-3.5 w-3.5" />
                            עריכה
                          </Button>
                          {!branch.is_main && (
                            <Button
                              variant="ghost"
                              size="sm"
                              loading={deactivateMutation.isPending}
                              onClick={() => {
                                if (confirm(`להעביר את הסניף "${branch.name}" לסטטוס לא פעיל?`)) {
                                  deactivateMutation.mutate(branch.id);
                                }
                              }}
                              className="text-danger hover:text-danger hover:bg-danger/10"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              מחיקה
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="lg:hidden p-3 space-y-3">
              {branches.map((branch) => (
                <div key={branch.id} className="bg-navy-dark border border-navy-border rounded-xl p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-ink-primary">{branch.name}</p>
                      {branch.is_main && <Badge variant="warning">ראשי</Badge>}
                    </div>
                    <Badge variant={statusVariant(branch.status)}>
                      {branch.status === 'active' ? 'פעיל' : branch.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-ink-secondary">
                    <div>קוד: <span className="num">{branch.code}</span></div>
                    <div>מדינה: {branch.country ?? '—'}</div>
                    <div>עיר: {branch.city ?? '—'}</div>
                    <div>מטבע: {branch.currency}</div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(branch)}>
                      <Edit2 className="h-3.5 w-3.5" />
                      עריכה
                    </Button>
                    {!branch.is_main && (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={deactivateMutation.isPending}
                        onClick={() => {
                          if (confirm(`להעביר את הסניף "${branch.name}" לסטטוס לא פעיל?`)) {
                            deactivateMutation.mutate(branch.id);
                          }
                        }}
                        className="text-danger hover:text-danger hover:bg-danger/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        מחיקה
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <BranchModal
        orgId={org.id}
        branch={editingBranch}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSaved={onSaved}
      />
    </div>
  );
}
